import {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  forwardRef,
} from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

/**
 * Form field primitives. The global stylesheet already gives every input the
 * border, radius and focus ring; these add sizing, invalid state and the
 * label / hint / error layout.
 */

const controlCss = `
  width: 100%;
  min-width: 0;
  height: 40px;
  padding: 0 12px;
  font-size: 14px;
  color: var(--color-text-primary);
  background-color: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: border-color var(--transition-normal), box-shadow var(--transition-normal);
  &:hover:not(:disabled):not(:focus) { border-color: var(--color-border-strong); }
  &:focus { outline: none; border-color: var(--color-black); box-shadow: var(--shadow-focus); }
  &[aria-invalid='true'] { border-color: var(--color-error); }
  &[aria-invalid='true']:focus { box-shadow: 0 0 0 4px rgb(239 68 68 / 0.15); }
  &:disabled { background-color: var(--color-bg-secondary); color: var(--color-text-tertiary); cursor: not-allowed; }
`;

export const Input = styled.input`
  ${controlCss}
`;

export const Select = styled.select`
  ${controlCss}
  padding-right: 36px;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' viewBox='0 0 24 24' stroke='%236B7280' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
`;

export const Textarea = styled.textarea`
  ${controlCss}
  height: auto;
  min-height: 96px;
  padding: 10px 12px;
  line-height: 1.5;
  resize: vertical;
`;

export const Label = styled.label`
  ${tw`block text-sm font-medium mb-1.5`}
  color: var(--color-text-primary);
`;

const Required = styled.span`
  color: var(--color-error);
  margin-left: 2px;
`;

export const Hint = styled.p`
  ${tw`mt-1.5 text-xs`}
  color: var(--color-text-secondary);
`;

export const ErrorText = styled.p`
  ${tw`mt-1.5 text-xs font-medium`}
  color: var(--color-error);
`;

const FieldRoot = styled.div`
  ${tw`flex flex-col min-w-0 w-full`}
`;

export interface FieldProps {
  label?: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function Field({ label, htmlFor, hint, error, required, children, className }: FieldProps) {
  return (
    <FieldRoot className={className}>
      {label && (
        <Label htmlFor={htmlFor}>
          {label}
          {required && <Required aria-hidden>*</Required>}
        </Label>
      )}
      {children}
      {error ? <ErrorText role="alert">{error}</ErrorText> : hint ? <Hint>{hint}</Hint> : null}
    </FieldRoot>
  );
}

/** Two-column form grid that collapses on small screens. */
export const FormGrid = styled.div<{ $cols?: 2 | 3 }>`
  ${tw`grid grid-cols-1 gap-4`}
  @media (min-width: 640px) {
    grid-template-columns: repeat(${({ $cols = 2 }) => $cols}, minmax(0, 1fr));
  }
`;

/** Input with a leading or trailing adornment (currency sign, unit, icon). */
const AdornedRoot = styled.div`
  ${tw`relative flex items-center w-full min-w-0`}
`;
const Adornment = styled.span<{ $side: 'left' | 'right' }>`
  ${tw`absolute inset-y-0 flex items-center text-sm pointer-events-none`}
  color: var(--color-text-secondary);
  ${({ $side }) => ($side === 'left' ? 'left: 12px;' : 'right: 12px;')}
`;

type AdornedInputProps = InputHTMLAttributes<HTMLInputElement> & {
  leading?: ReactNode;
  trailing?: ReactNode;
};

export const AdornedInput = forwardRef<HTMLInputElement, AdornedInputProps>(function AdornedInput(
  { leading, trailing, style, ...rest },
  ref,
) {
  return (
    <AdornedRoot>
      {leading && <Adornment $side="left">{leading}</Adornment>}
      <Input
        ref={ref}
        {...rest}
        style={{
          ...(leading ? { paddingLeft: 30 } : null),
          ...(trailing ? { paddingRight: 44 } : null),
          ...style,
        }}
      />
      {trailing && <Adornment $side="right">{trailing}</Adornment>}
    </AdornedRoot>
  );
});

export type InputProps = InputHTMLAttributes<HTMLInputElement>;
export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;
export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;
